
import { TikTokVideo } from "@/types/api";
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { User, Trash2 } from "lucide-react";
import { getAuthorDisplayName, formatDuration } from "@/utils/videoHelpers";
import { format } from "date-fns";

interface VideoTableRowProps {
  video: TikTokVideo;
  isChecked: boolean;
  onToggleSelect: (id: string) => void;
  onDeleteVideo?: (id: string) => void;
  order?: number;
}

export function VideoTableRow({
  video,
  isChecked,
  onToggleSelect,
  onDeleteVideo,
}: VideoTableRowProps) {
  const authorName = getAuthorDisplayName(video.author);
  const videoUrl = `https://www.tiktok.com/@${authorName}/video/${video.id}`;
  
  // Format the date - since createTime doesn't exist, we'll use a placeholder
  const formattedDate = "-"; // Default value if no date available
  
  // Format stats with comma separators
  const formatStat = (stat?: number) => stat ? stat.toLocaleString() : "-";

  // Get stats from the stats object in the TikTokVideo interface
  const views = video.stats?.playCount;
  const comments = video.stats?.commentCount;
  const likes = video.stats?.likeCount;
  const shares = video.stats?.shareCount;
  const bookmarks = video.stats?.collectCount;

  return (
    <TableRow className="border-b border-gray-800/20 hover:bg-[#15192a]/70">
      <TableCell className="w-[40px] py-4">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => onToggleSelect(video.id)}
          className="accent-[#00E5FF] h-4 w-4 rounded border-gray-600 bg-transparent"
        />
      </TableCell>
      <TableCell className="py-4">
        <div className="relative w-12 h-16 rounded overflow-hidden">
          <img
            src={video.thumbnail}
            alt="Video thumbnail"
            className="w-full h-full object-cover"
            onError={(e) => ((e.target as HTMLImageElement).src = "/placeholder.svg")}
          />
        </div>
      </TableCell>
      <TableCell className="py-4">
        <div className="flex flex-col">
          <p className="truncate text-sm text-white max-w-[200px] font-medium">{video.description}</p>
          <span className="text-xs text-[#00E5FF] mt-1">{authorName}</span>
        </div>
      </TableCell>
      <TableCell className="py-4 text-right text-sm text-gray-300">{formatStat(views)}</TableCell>
      <TableCell className="py-4 text-right text-sm text-gray-300">{formatStat(comments)}</TableCell>
      <TableCell className="py-4 text-right text-sm text-gray-300">{formatStat(likes)}</TableCell>
      <TableCell className="py-4 text-right text-sm text-gray-300">{formatStat(shares)}</TableCell>
      <TableCell className="py-4 text-right text-sm text-gray-300">{formatStat(bookmarks)}</TableCell>
      <TableCell className="py-4 text-right text-sm text-gray-300">
        {formatDuration(video.duration)}
      </TableCell>
      <TableCell className="py-4 text-right text-sm text-gray-300">
        {formattedDate}
      </TableCell>
      <TableCell className="py-4">
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDeleteVideo?.(video.id)}
            className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-400/10"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
